import React, { memo, useMemo, useState } from 'react';
import { Box, InputAdornment, TextField, Typography, alpha } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';

export interface EntityItem {
  id: string;
  label: string;
  detail?: string;
  /** Periods still to place (draft only). */
  pending?: number;
  /** Extra content under the label, e.g. load bars. */
  extra?: React.ReactNode;
  /** A problem with this item, shown as a red dot with the text on hover. */
  alert?: string;
}

interface EntityListProps {
  items: EntityItem[];
  selected: string | null;
  onSelect: (id: string) => void;
  placeholder: string;
  /** Leave out the list's own search (when the page already has one). */
  hideSearch?: boolean;
}

/** The classes, teachers or rooms to look at, with what each still needs placed. */
const EntityList = memo<EntityListProps>(({ items, selected, onSelect, placeholder, hideSearch }) => {
  const [q, setQ] = useState('');
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return needle ? items.filter((i) => i.label.toLowerCase().includes(needle)) : items;
  }, [items, q]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
      {!hideSearch && items.length > 8 && (
        <TextField
          size="small"
          placeholder={placeholder}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} /></InputAdornment> }}
          sx={{ mb: 1 }}
        />
      )}
      <Box sx={{ overflowY: 'auto', flex: 1, mx: -0.5, px: 0.5 }}>
        {shown.map((item) => {
          const active = item.id === selected;
          return (
            <Box
              key={item.id}
              role="button"
              tabIndex={0}
              onClick={() => onSelect(item.id)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(item.id); } }}
              sx={(theme) => ({
                display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.85, my: 0.25, borderRadius: '10px', cursor: 'pointer', outline: 'none',
                bgcolor: active ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                '&:hover': { bgcolor: active ? alpha(theme.palette.primary.main, 0.12) : 'action.hover' },
                '&:focus-visible': { boxShadow: `inset 0 0 0 2px ${alpha(theme.palette.primary.main, 0.5)}` },
              })}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap sx={{ fontWeight: active ? 700 : 500, color: active ? 'primary.main' : 'text.primary' }}>
                  {item.label}
                </Typography>
                {item.detail && <Typography variant="caption" noWrap component="div" color="text.secondary">{item.detail}</Typography>}
                {item.extra}
              </Box>
              {item.alert && (
                <Box title={item.alert} sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main', flexShrink: 0 }} />
              )}
              {!!item.pending && (
                <Box
                  title={`${item.pending} period${item.pending === 1 ? '' : 's'} to place`}
                  sx={(theme) => ({
                    flexShrink: 0, minWidth: 22, height: 20, px: 0.75, borderRadius: '10px', display: 'grid', placeItems: 'center',
                    fontSize: '0.68rem', fontWeight: 700, color: theme.palette.warning.dark, bgcolor: alpha(theme.palette.warning.main, 0.16),
                  })}
                >
                  {item.pending}
                </Box>
              )}
            </Box>
          );
        })}
        {!shown.length && <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 2 }}>Nothing matches.</Typography>}
      </Box>
    </Box>
  );
});

EntityList.displayName = 'EntityList';

export default EntityList;
